import { parseWithZod } from '@conform-to/zod'
import { InquirySource } from '@prisma/client'
import {
  ActionFunctionArgs,
  json,
  LoaderFunctionArgs,
  MetaFunction,
  redirect,
} from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
// eslint-disable-next-line @typescript-eslint/naming-convention
import * as Sentry from '@sentry/react'

import { NO_IMAGE_URL } from '~/constants'
import {
  officeContactFormSchema,
  OfficeContactInfoCard,
  OfficeContactPage,
} from '~/features'
import { CustomError } from '~/lib'
import { createOfficeInquiry } from '~/models/inquiry.server'
import { findOfficeById } from '~/models/office.server'
import { Office, Transportation } from '~/models/types'

type OfficeInfo = React.ComponentProps<typeof OfficeContactInfoCard>

// HACK: オフィス検索結果一覧でも同じ処理をしている。共通化する。
const convertTransportationToText = (transportation: Transportation) => {
  switch (transportation) {
    case 'WALK':
      return '徒歩'
    case 'BUS':
      return 'バス'
    default:
      return ''
  }
}

const createTransportationInfo = (stations: Office['stations']) => {
  return stations
    .map((station) => {
      return `${station.name}駅${convertTransportationToText(station.transportation)}${station.time}分`
    })
    .join('、')
}

const transformOfficeInfo = (office: Office): OfficeInfo => {
  const sortedImages = office?.images.sort((a, b) => a.priority - b.priority)
  const firstImage =
    sortedImages &&
    (sortedImages.find((image) => image.isEyeCatch) || sortedImages[0])?.url
  return {
    officeName: office.name,
    address: office.address,
    transportationInfo: createTransportationInfo(office.stations),
    imageSrc: firstImage || NO_IMAGE_URL,
  }
}

export const loader = async ({ params }: LoaderFunctionArgs) => {
  const { officeId } = params
  if (!officeId) throw new Response('Bad Request', { status: 400 })

  const office = await findOfficeById(Number(officeId))

  if (!office) {
    throw new Response('Office Not Found', {
      status: 404,
      statusText: 'Office Not Found.',
    })
  }

  return json({
    officeContactInfo: transformOfficeInfo(office),
  })
}

export async function action({ request, params }: ActionFunctionArgs) {
  const { officeId, prefectureSlug } = params
  if (!officeId || !prefectureSlug) {
    throw new Response('Bad Request', { status: 400 })
  }

  const formData = await request.formData()

  const submission = parseWithZod(formData, {
    schema: officeContactFormSchema,
  })

  if (submission.status !== 'success') {
    return submission.reply()
  }

  try {
    const result = await createOfficeInquiry({
      name: submission.value.name,
      organization: submission.value.organization,
      email: submission.value.email,
      phone: submission.value.phone,
      numberOfPeople: submission.value.numberOfPeople ?? '',
      desiredMoveInDate: submission.value.desiredMoveInDate ?? '',
      budget: submission.value.budget ?? '',
      content: submission.value.content ?? '',
      officeId: Number(officeId),
      inquirySource: InquirySource.OFFICE_PAGE,
      prefectureSlug,
    })

    if (!result.success) {
      throw new CustomError(result.message, {
        statusCode: result.statusCode,
      })
    }

    return redirect('/contact-success')
  } catch (error: unknown) {
    console.error('error', error)
    Sentry.captureException(error, {
      extra: {
        route: `/prefectures/${prefectureSlug}/offices/${officeId}/contact`,
      },
    })

    if (error instanceof CustomError) {
      throw new Response(null, {
        status: error.statusCode,
      })
    }

    throw new Response(null, {
      status: 500,
    })
  }
}

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  const officeName = data?.officeContactInfo?.officeName
  const titlePrefix = officeName
    ? `${officeName}へのお問い合わせ`
    : 'お問い合わせ'
  const parentMeta = matches
    .flatMap((match) => match.meta ?? [])
    .filter((meta) => !('title' in meta))
  return [
    ...parentMeta,
    { title: titlePrefix + ' | シェアオフィス コワーキングスペース検索サイト' },
  ]
}

export default function Index() {
  const { officeContactInfo } = useLoaderData<typeof loader>()

  return <OfficeContactPage officeContactInfo={officeContactInfo} />
}
